/**
 * Geocoding service for extracting coordinates from UAE address information
 * Now supports browser geolocation and HERE Maps API reverse geocoding
 */

export interface Address {
  emirate: string;
  community: string;
  street: string;
  building_name_num: string;
}

export interface Coordinates {
  lat: number;
  long: number;
}

export interface ReverseGeocodingResult {
  street?: string;
  community?: string;
  building_number?: string;
  emirate?: string;
  formatted_address?: string;
}

// Dummy coordinates for each emirate as fallback
const EMIRATE_DUMMY_COORDINATES: Record<string, Coordinates> = {
  'Dubai': { lat: 25.2048, long: 55.2708 },
  'Abu Dhabi': { lat: 24.4539, long: 54.3773 },
  'Sharjah': { lat: 25.3463, long: 55.4209 },
  'Ajman': { lat: 25.4052, long: 55.5136 },
  'Fujairah': { lat: 25.1288, long: 56.3265 },
  'Ras Al Khaimah': { lat: 25.7889, long: 55.9758 },
  'Umm Al Quwain': { lat: 25.5644, long: 55.6855 },
  'Al Ain': { lat: 24.2075, long: 55.7447 },
};

// Well-known community coordinates for better accuracy
const COMMUNITY_COORDINATES: Record<string, Coordinates> = {
  // Dubai communities
  'Dubai Marina': { lat: 25.0800, long: 55.1421 },
  'Downtown Dubai': { lat: 25.1972, long: 55.2744 },
  'Business Bay': { lat: 25.1877, long: 55.2633 },
  'JBR': { lat: 25.0830, long: 55.1373 },
  'Jumeirah Beach Residence': { lat: 25.0830, long: 55.1373 },
  'DIFC': { lat: 25.2138, long: 55.2833 },
  'Dubai International Financial Centre': { lat: 25.2138, long: 55.2833 },
  'Palm Jumeirah': { lat: 25.1124, long: 55.1390 },
  'Jumeirah Lake Towers': { lat: 25.0704, long: 55.1441 },
  'JLT': { lat: 25.0704, long: 55.1441 },
  'Dubai Sports City': { lat: 25.0426, long: 55.1160 },
  'Motor City': { lat: 25.0450, long: 55.1200 },
  'Dubai Hills Estate': { lat: 25.1122, long: 55.2506 },
  'Arabian Ranches': { lat: 25.0548, long: 55.2706 },
  'Emirates Hills': { lat: 25.1150, long: 55.1850 },
  'The Greens': { lat: 25.0970, long: 55.1650 },
  'The Views': { lat: 25.0980, long: 55.1680 },
  'Discovery Gardens': { lat: 25.0430, long: 55.1270 },
  'International City': { lat: 25.1670, long: 55.4170 },
  'Deira': { lat: 25.2697, long: 55.3094 },
  'Bur Dubai': { lat: 25.2582, long: 55.2962 },
  'Jumeirah': { lat: 25.2226, long: 55.2593 },
  'Al Barsha': { lat: 25.1138, long: 55.1961 },
  'Dubai Silicon Oasis': { lat: 25.1272, long: 55.3836 },
  'Dubai South': { lat: 24.8956, long: 55.1611 },
  'Mirdif': { lat: 25.2173, long: 55.4106 },
  'Dubailand': { lat: 25.0550, long: 55.2950 },
  
  // Abu Dhabi communities
  'Corniche': { lat: 24.4764, long: 54.3705 },
  'Al Reem Island': { lat: 24.4975, long: 54.3947 },
  'Saadiyat Island': { lat: 24.5394, long: 54.4375 },
  'Yas Island': { lat: 24.4672, long: 54.6033 },
  'Al Reef': { lat: 24.4200, long: 54.6150 },
  'Khalifa City': { lat: 24.4189, long: 54.5975 },
  'Al Raha Beach': { lat: 24.4417, long: 54.6472 },
  'Marina Village': { lat: 24.4450, long: 54.6500 },
  
  // Sharjah communities
  'Al Nahda': { lat: 25.3050, long: 55.3780 },
  'Muwaileh': { lat: 25.2900, long: 55.4200 },
  'Al Majaz': { lat: 25.3350, long: 55.3850 },
  'Al Khan': { lat: 25.3400, long: 55.3900 },
  'Al Qasimia': { lat: 25.3500, long: 55.4000 },
  
  // Other emirates - major areas
  'Ajman Downtown': { lat: 25.4052, long: 55.5136 },
  'Al Nuaimiya': { lat: 25.3950, long: 55.4850 },
  'Fujairah City': { lat: 25.1288, long: 56.3265 },
  'RAK City': { lat: 25.7889, long: 55.9758 },
  'UAQ Downtown': { lat: 25.5644, long: 55.6855 },
  'Al Ain City': { lat: 24.2075, long: 55.7447 },
};

/**
 * Gets the HERE API key from environment variables
 */
function getHereApiKey(): string {
  return "example_api_key";
}

/**
 * Gets the user's current location using browser geolocation API
 */
export function getCurrentLocation(): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords: Coordinates = {
          lat: position.coords.latitude,
          long: position.coords.longitude
        };
        // Browser geolocation successful
        resolve(coords);
      },
      (error) => {
        console.warn('Browser geolocation failed:', error.message);
        reject(new Error(`Geolocation failed: ${error.message}`));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 600000 // 10 minutes
      }
    );
  });
}

/**
 * Reverse geocode coordinates using HERE Maps API to get address details
 */
export async function reverseGeocode(coordinates: Coordinates): Promise<ReverseGeocodingResult | null> {
  try {
    const apiKey = getHereApiKey();
    const { lat, long } = coordinates;

    const response = await fetch(
      `https://revgeocode.search.hereapi.com/v1/revgeocode?at=${lat},${long}&lang=en-US&apiKey=${apiKey}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      }
    );

    if (!response.ok) {
      console.warn('HERE Reverse Geocoding API request failed:', response.status);
      return null;
    }

    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      const result = data.items[0];
      const address = result.address;
      
      console.log('HERE Reverse Geocoding successful:', {
        coordinates,
        address: result.address,
        title: result.title
      });
      
      // Extract relevant components for UAE addresses
      const reverseResult: ReverseGeocodingResult = {
        street: address.street || address.label,
        community: address.district || address.subdistrict || address.city,
        building_number: address.houseNumber,
        emirate: address.state || address.county,
        formatted_address: result.title || result.address.label
      };
      
      return reverseResult;
    } else {
      console.warn('HERE Reverse Geocoding: No results found for coordinates:', coordinates);
      return null;
    }

  } catch (error) {
    console.warn('HERE Reverse Geocoding error:', error);
    return null;
  }
}

/**
 * Gets location and address information automatically using browser geolocation and HERE reverse geocoding
 */
export async function getLocationAndAddress(): Promise<{ coordinates: Coordinates; address: ReverseGeocodingResult | null }> {
  try {
    // First, try to get the user's current location from browser
    const coordinates = await getCurrentLocation();
    
    // Then reverse geocode to get address details
    const addressDetails = await reverseGeocode(coordinates);
    
    return {
      coordinates,
      address: addressDetails
    };
  } catch (error) {
    console.warn('Failed to get location and address:', error);
    throw error;
  }
}

/**
 * Forward geocoding using HERE Maps API for addresses when needed
 */
async function geocodeAddress(address: Address): Promise<Coordinates | null> {
  try {
    const apiKey = getHereApiKey();
    
    // Create a search query combining all address components
    const query = [
      address.building_name_num,
      address.street,
      address.community,
      address.emirate,
      'UAE'
    ].filter(Boolean).join(', ');

    const response = await fetch(
      `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(query)}&countryCode=AE&lang=en-US&apiKey=${apiKey}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      }
    );

    if (!response.ok) {
      console.warn('HERE Geocoding API request failed:', response.status);
      return null;
    }

    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      const result = data.items[0];
      const position = result.position;
      
      console.log('HERE Geocoding successful:', {
        query,
        title: result.title,
        coordinates: position
      });
      
      return {
        lat: position.lat,
        long: position.lng
      };
    } else {
      console.warn('HERE Geocoding: No results found for address:', query);
      return null;
    }

  } catch (error) {
    console.warn('HERE Geocoding error:', error);
    return null;
  }
}

/**
 * Gets coordinates for a community if it's in our known list
 */
function getCommunityCoordinates(community: string): Coordinates | null {
  // Try exact match first
  if (COMMUNITY_COORDINATES[community]) {
    return COMMUNITY_COORDINATES[community];
  }
  
  // Try case-insensitive match
  const lowerCommunity = community.toLowerCase();
  for (const [key, coords] of Object.entries(COMMUNITY_COORDINATES)) {
    if (key.toLowerCase() === lowerCommunity) {
      return coords;
    }
  }
  
  // Try partial match for common abbreviations
  for (const [key, coords] of Object.entries(COMMUNITY_COORDINATES)) {
    if (key.toLowerCase().includes(lowerCommunity) || lowerCommunity.includes(key.toLowerCase())) {
      return coords;
    }
  }
  
  return null;
}

/**
 * Gets dummy coordinates for an emirate
 */
function getEmirateCoordinates(emirate: string): Coordinates {
  return EMIRATE_DUMMY_COORDINATES[emirate] || EMIRATE_DUMMY_COORDINATES['Dubai'];
}

/**
 * Main function to extract coordinates from address information
 * Uses multiple fallback strategies for maximum reliability
 */
export async function extractCoordinates(address: Address): Promise<Coordinates> {
  console.log('Extracting coordinates for address:', address);
  
  try {
    // Strategy 1: Try geocoding the full address using HERE Geocoding API
    const geocodedCoords = await geocodeAddress(address);
    if (geocodedCoords) {
      // Successfully geocoded address with HERE API
      return geocodedCoords;
    }
    
    // Strategy 2: Try to find coordinates for known community
    const communityCoords = getCommunityCoordinates(address.community);
    if (communityCoords) {
      console.log('Using known community coordinates:', communityCoords);
      return communityCoords;
    }
    
    // Strategy 3: Fallback to emirate center coordinates
    const emirateCoords = getEmirateCoordinates(address.emirate);
    console.log('Using emirate fallback coordinates:', emirateCoords);
    return emirateCoords;
    
  } catch (error) {
    console.error('Error in coordinate extraction:', error);
    // Ultimate fallback to emirate coordinates
    const emirateCoords = getEmirateCoordinates(address.emirate);
    console.log('Using emirate coordinates as ultimate fallback:', emirateCoords);
    return emirateCoords;
  }
}

/**
 * Validates if coordinates are within reasonable bounds for UAE
 */
export function validateUAECoordinates(coords: Coordinates): boolean {
  // UAE rough bounds: lat 22-26, long 51-57
  return coords.lat >= 22 && coords.lat <= 26 && coords.long >= 51 && coords.long <= 57;
}